import { CSSProperties } from 'react';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { IPersonStatus } from '~/utils/interface/map';
import { adminRouteAbsolute } from '~/utils/constants/route';
import styles from '../Map.module.scss';
import type { TrackingDevice } from '../mapTrackingTypes';
import { getStatusColor, getStatusLabel, getStatusStyleVars } from '../statusPerson';

const cx = classNames.bind(styles);

interface UserDetailPopupProps {
  user: TrackingDevice;
  onClose: () => void;
  personStatuses: IPersonStatus[];
}

interface DetailRowProps {
  label: string;
  value: string;
}

function DetailRow({ label, value }: DetailRowProps) {
  return (
    <div className='grid grid-cols-[156px_minmax(0,1fr)] gap-2 py-1.5'>
      <span className='font-["Manrope",sans-serif] text-xs font-normal leading-[1.4] tracking-normal text-[#7C8796] whitespace-nowrap'>
        {label}:
      </span>
      <span className='font-["Manrope",sans-serif] text-[13px] font-normal leading-[1.4] tracking-normal text-[#1F2937] break-words'>
        {value}
      </span>
    </div>
  );
}

function UserDetailPopup({ user, onClose, personStatuses }: UserDetailPopupProps) {
  const { t, i18n } = useTranslation('admin/map');
  const navigate = useNavigate();
  const statusBorderColor = getStatusColor(user.status, personStatuses);
  const statusStyleVars = getStatusStyleVars(user.status, personStatuses);
  const accentStyle = {
    ...statusStyleVars,
    fontFamily: '"Manrope", sans-serif',
    ...(statusBorderColor ? { borderColor: statusBorderColor } : {}),
  } as CSSProperties;
  const na = t('common.notAvailable');
  const batteryPercent =
    typeof user.battery_percent === 'number' && Number.isFinite(user.battery_percent) ? `${user.battery_percent}%` : na;
  const todayDistance =
    user.today_distance !== '--' && Number.isFinite(Number(user.today_distance))
      ? `${Number(user.today_distance).toFixed(2)} km`
      : na;

  return (
    <div
      className={cx(
        'UserDetailPopup',
        'user-detail-popup-card',
        'w-[320px] rounded-xl border-2 bg-white shadow-xl p-3'
      )}
      style={accentStyle}
    >
      <div className='flex items-start justify-between gap-2'>
        <h3
          className={cx('user-detail-popup-title', 'text-sm font-semibold truncate leading-5')}
          title={user.full_name}
        >
          {user.full_name}
        </h3>
        <button
          type='button'
          onClick={onClose}
          className='h-6 w-6 rounded-md border border-[#D7DEE7] text-[#72777B] text-xs leading-none hover:bg-[#F5F7FA]'
          aria-label={t('popup.close')}
        >
          {t('popup.closeMark')}
        </button>
      </div>

      <div
        className={cx('user-detail-popup-divider', '-mx-3 my-2 border-t')}
        style={(statusBorderColor ? { borderTopColor: statusBorderColor } : undefined) as CSSProperties | undefined}
      />

      <DetailRow label={t('popup.cccd')} value={user.cccd || na} />
      <DetailRow label={t('popup.deviceImei')} value={user.device_imei || na} />
      <DetailRow label={t('popup.battery')} value={batteryPercent} />
      <DetailRow label={t('popup.todayDistance')} value={todayDistance} />
      <DetailRow
        label={t('popup.status')}
        value={getStatusLabel(user.status, personStatuses, t('status.unknown'), i18n.language)}
      />
      <DetailRow label={t('popup.lastAddress')} value={user.address || na} />
      <DetailRow label={t('popup.lastTime')} value={user.trktime || na} />

      <button
        type='button'
        onClick={() => {
          navigate(adminRouteAbsolute.mapbackJourney.replace(':personId', String(user.person_id)));
        }}
        className={cx(
          'btn-view-route',
          'mt-3 w-full flex items-center justify-center gap-2 rounded-xl transition-opacity hover:opacity-90'
        )}
      >
        {t('popup.viewRoute')}
      </button>
    </div>
  );
}

export default UserDetailPopup;
